import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiFormItemComponent } from '@siemens/element-ng/form';
import { SelectItem, SiSelectModule, SiSelectOptionTemplateDirective } from '@siemens/element-ng/select';
import { Kpi, Unit } from '@tran-monorepo/interfaces/applications';
import { LanguageService } from '../../../services/language.service';
import { CustomValueSelectFieldComponent } from '../custom-value-select/custom-value-select-field.component';
import { NumberInputComponent } from '../number-input';
import { TextareaFieldComponent } from '../textarea-field/textarea-field.component';
import { KpiSelectorFormGroup } from './kpi-selector-form.model';

@Component({
    selector: 'tran-kpi-selector',
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        SiFormItemComponent,
        SiSelectModule,
        SiSelectOptionTemplateDirective,
        NumberInputComponent,
        TextareaFieldComponent,
        CustomValueSelectFieldComponent
    ],
    templateUrl: './kpi-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KpiSelectorComponent {
    private readonly translate = inject(TranslateService);
    private readonly language = inject(LanguageService);

    public readonly form = input.required<KpiSelectorFormGroup>();
    public readonly kpis = input<Kpi[]>([]);
    public readonly title = input<string>('');
    public readonly description = input<string>('');
    public readonly submitted = input(false);
    public readonly readonly = input(false);

    public readonly units = input<Unit[]>([]);

    protected readonly sortedKpis = computed(() => {
        const language = this.language.currentLanguage();
        const collator = new Intl.Collator(language, { sensitivity: 'base' });

        return [...this.kpis()].sort((left, right) =>
            collator.compare(this.translate.instant(`kpis.${left.name}`), this.translate.instant(`kpis.${right.name}`))
        );
    });

    protected readonly unitOptions = computed<SelectItem<string>[]>(() => {
        return this.units().map((unit: Unit) => ({
            type: 'option' as const,
            value: unit.id,
            label: `units.${unit.name}`
        }));
    });

    protected readonly requiredValidator = Validators.required;

    protected unitSymbol(unitId: string): string {
        return this.units().find(unit => unit.id === unitId)?.symbol ?? '';
    }

    constructor() {
        effect(() => {
            if (this.submitted()) this.form().controls.selection.markAsTouched();
        });
    }
}
